/**
 * A comment, drawn.
 *
 * The parser in lib/markdown.ts hands us data; this file is the only place that
 * decides what that data LOOKS like. Nothing here builds an HTML string, so
 * there is no `dangerouslySetInnerHTML` in Lattice and a comment cannot inject
 * anything into the page — the worst a hostile body can do is look odd.
 *
 * WHY A FILE REFERENCE IS A COMPONENT AND NOT A LINK
 *   "[[Gun Assembly/Roof.PDF]]" names a path, and paths move. The chip resolves
 *   the reference against the change log every time it renders (see
 *   `followRenames`), so a note written in April still opens the right drawing
 *   in September, and a reference to something genuinely gone says so instead
 *   of quietly failing when clicked.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { FileWarning } from 'lucide-react'
import { followRenames, basename, type Path, type ProjectId, type UserId } from '@lattice/core'
import { useWorkspace, filesByProject, personOf } from '@/store/workspace'
import { useUi } from '@/store/ui'
import { parseMarkdown, type Block, type Inline } from '@/lib/markdown'
import { FileIcon } from './FileIcon'
import { Tooltip } from './ui/primitives'
import { cn } from '@/lib/utils'

/**
 * Taller than this and a comment is collapsed behind "Show more".
 * Interpretation: about twelve lines — enough for a complete thought, short
 * enough that one person pasting a specification cannot bury the conversation.
 */
const COLLAPSE_ABOVE_PX = 260

/**
 * Render one comment body.
 *
 * `seq` is the comment's position in the log, which is what lets a file
 * reference be followed through renames that happened AFTER it was written.
 */
export function CommentBody({ body, projectId, seq }: {
  body: string
  projectId: ProjectId
  seq: number
}) {
  const blocks = useMemo(() => parseMarkdown(body), [body])
  const [expanded, setExpanded] = useState(false)
  const [overflows, setOverflows] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Measure rather than count characters: what matters is whether it is tall on
  // screen, and a table of dimensions is tall at half the length of prose.
  //
  // It must be measured AGAIN whenever the column's width changes. A comment
  // panel that mounts narrow (or off-screen, before the pane is laid out) and
  // then widens would otherwise keep the verdict it reached at the wrong width,
  // and offer "Show more" on a single line of text.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = (): void => setOverflows(el.scrollHeight > COLLAPSE_ABOVE_PX + 24)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [body])

  const collapsed = overflows && !expanded

  return (
    <div className="mt-0.5">
      <div
        ref={ref}
        className={cn('space-y-1.5 text-sm leading-relaxed', collapsed && 'relative overflow-hidden')}
        style={collapsed ? { maxHeight: COLLAPSE_ABOVE_PX } : undefined}
      >
        {blocks.map((block, i) => <BlockView key={i} block={block} projectId={projectId} seq={seq} />)}
        {collapsed && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
            style={{ background: 'linear-gradient(to bottom, transparent, var(--bg))' }}
          />
        )}
      </div>
      {overflows && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="snap mt-0.5 text-xs font-medium text-[var(--accent)] hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Blocks                                                              */
/* ------------------------------------------------------------------ */

const HEADING_SIZE: Record<number, string> = {
  1: 'text-base font-semibold',
  2: 'text-[14px] font-semibold',
  3: 'text-sm font-semibold',
  4: 'text-sm font-semibold',
  5: 'text-sm font-medium',
  6: 'text-sm font-medium',
}

function BlockView({ block, projectId, seq }: { block: Block; projectId: ProjectId; seq: number }) {
  const inline = (nodes: readonly Inline[]) =>
    nodes.map((node, i) => <InlineView key={i} node={node} projectId={projectId} seq={seq} />)

  switch (block.kind) {
    case 'paragraph':
      return <p className="break-words">{inline(block.children)}</p>

    case 'heading':
      return (
        <p className={cn('mt-2 break-words first:mt-0', HEADING_SIZE[block.level] ?? HEADING_SIZE[6])}>
          {inline(block.children)}
        </p>
      )

    case 'code':
      return (
        <pre className="overflow-x-auto rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-inset)] p-2">
          <code className="font-mono text-xs leading-relaxed">{block.text}</code>
        </pre>
      )

    case 'quote':
      return (
        <blockquote className="space-y-1.5 border-l-2 border-[var(--border-strong)] pl-2 text-[var(--fg-muted)]">
          {block.blocks.map((b, i) => <BlockView key={i} block={b} projectId={projectId} seq={seq} />)}
        </blockquote>
      )

    case 'list': {
      const items = block.items.map((item, i) => (
        <li key={i} className="break-words">{inline(item)}</li>
      ))
      return block.ordered
        ? <ol start={block.start} className="list-decimal space-y-0.5 pl-5 marker:text-[var(--fg-subtle)]">{items}</ol>
        : <ul className="list-disc space-y-0.5 pl-5 marker:text-[var(--fg-subtle)]">{items}</ul>
    }
  }
}

/* ------------------------------------------------------------------ */
/* Inlines                                                             */
/* ------------------------------------------------------------------ */

function InlineView({ node, projectId, seq }: { node: Inline; projectId: ProjectId; seq: number }) {
  const children = (nodes: readonly Inline[]) =>
    nodes.map((child, i) => <InlineView key={i} node={child} projectId={projectId} seq={seq} />)

  switch (node.kind) {
    case 'text':
      return <>{node.text}</>

    case 'break':
      return <br />

    case 'code':
      return (
        <code className="rounded-[3px] bg-[var(--bg-inset)] px-1 py-px font-mono text-[0.92em] text-[var(--fg)]">
          {node.text}
        </code>
      )

    case 'strong':
      return <strong className="font-semibold">{children(node.children)}</strong>

    case 'em':
      return <em className="italic">{children(node.children)}</em>

    case 'link':
      return (
        <a
          href={node.href}
          target="_blank"
          rel="noreferrer noopener"
          className="snap text-[var(--accent)] underline decoration-[var(--border-strong)] underline-offset-2 hover:decoration-[var(--accent)]"
        >
          {children(node.children)}
        </a>
      )

    case 'mention':
      return <MentionChip userId={node.userId} />

    case 'file':
      return <FileChip path={node.path} projectId={projectId} seq={seq} />
  }
}

/** A person, named the way the roster names them rather than as they were typed. */
function MentionChip({ userId }: { userId: UserId }) {
  const roster = useWorkspace(s => s.roster)
  const me = useWorkspace(s => s.user)
  const person = personOf(roster, userId)
  return (
    <Tooltip label={person.email}>
      <span
        className={cn(
          'rounded-[3px] px-1 font-medium',
          me?.id === userId
            ? 'bg-[var(--accent)] text-[var(--fg-onAccent)]'
            : 'bg-[var(--accent-soft)] text-[var(--accent)]',
        )}
      >
        @{person.name.split(' ')[0]}
      </span>
    </Tooltip>
  )
}

/**
 * A reference to a file, as a chip that opens it.
 *
 * Three states, and the difference between them is the whole point: here,
 * moved (we followed it), and gone (we say so and do not pretend to open it).
 */
export function FileChip({ path, projectId, seq }: { path: Path; projectId: ProjectId; seq: number }) {
  const changes = useWorkspace(s => s.changes)
  const { go, open } = useUi()

  const current = useMemo(
    () => followRenames(changes, projectId, path, seq),
    [changes, projectId, path, seq],
  )
  const exists = useMemo(
    () => filesByProject(changes).get(projectId)?.has(current.toLowerCase()) ?? false,
    [changes, projectId, current],
  )
  const moved = current !== path

  if (!exists) {
    return (
      <Tooltip label={`${path} is not in this project any more`}>
        <span className="inline-flex max-w-full translate-y-px items-baseline gap-1 rounded-[var(--radius-sm)] bg-[var(--bg-inset)] px-1 align-baseline text-[var(--fg-subtle)] line-through decoration-[var(--fg-subtle)]">
          <FileWarning className="h-3 w-3 shrink-0 translate-y-0.5" strokeWidth={1.75} />
          {basename(path)}
        </span>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={moved ? `${current} — renamed since this comment` : current}>
      <button
        onClick={() => { go({ projectId, view: 'files' }); open(current) }}
        className="snap inline-flex max-w-full translate-y-px items-baseline gap-1 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--bg-subtle)] px-1 align-baseline font-medium text-[var(--fg)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)]"
      >
        <FileIcon path={current} className="h-3 w-3 translate-y-0.5" />
        <span className="truncate">{basename(current)}</span>
      </button>
    </Tooltip>
  )
}
